// Main-screen banner for a lost full-screen-intent grant (Android 14+).
// Some OEMs (ColorOS on the field devices) silently reset the grant on EVERY
// app update — including our own self-update — so relying on the user to
// visit Settings → Notifications is not enough: this surfaces the loss on the
// screen they actually land on after updating, with a one-tap fix. Renders
// nothing on web or while the grant is fine; re-checks on refocus so it
// disappears the moment the toggle is flipped.

import { useEffect, useState, type ReactNode } from 'react';
import { useT } from '../../i18n/index.js';
import { isCapacitorNative } from '../../shell/capacitor/platform.js';

/** Test seam: query + open the native full-screen-intent settings screen. */
export interface FsiBannerBridge {
  getStatus(): Promise<{ needsAttention: boolean }>;
  openSettings(): Promise<unknown>;
}

function createBridge(): FsiBannerBridge {
  // Cache the MODULE, never a plugin proxy (see NotificationsSettingsScreen).
  let mod: Promise<typeof import('../../shell/capacitor/index.js')> | undefined;
  const getModule = (): NonNullable<typeof mod> =>
    (mod ??= import('../../shell/capacitor/index.js'));
  return {
    async getStatus() {
      const native = await getModule();
      return native.nativePlugins().callNotification.getFullScreenIntentStatus();
    },
    async openSettings() {
      const native = await getModule();
      return native.nativePlugins().callNotification.openFullScreenIntentSettings();
    },
  };
}

export function FsiAttentionBanner({ bridge }: { bridge?: FsiBannerBridge } = {}): ReactNode {
  if (!bridge && !isCapacitorNative()) return null;
  return <Resolved bridge={bridge} />;
}

function Resolved({ bridge }: { bridge: FsiBannerBridge | undefined }): ReactNode {
  const [resolved] = useState<FsiBannerBridge>(() => bridge ?? createBridge());
  return <Banner bridge={resolved} />;
}

function Banner({ bridge }: { bridge: FsiBannerBridge }): ReactNode {
  const t = useT();
  const [needsAttention, setNeedsAttention] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const check = (): void => {
      void bridge
        .getStatus()
        .then(({ needsAttention }) => {
          if (!cancelled) setNeedsAttention(needsAttention);
        })
        .catch(() => {
          // Older bridge without the method: no banner, no false alarm.
          if (!cancelled) setNeedsAttention(false);
        });
    };
    check();
    const onFocus = (): void => {
      if (document.visibilityState === 'hidden') return;
      check();
    };
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('focus', onFocus);
    };
  }, [bridge]);

  if (!needsAttention) return null;
  return (
    <div
      role="status"
      className="flex items-center justify-between gap-3 rounded-xl bg-surface p-3 text-xs text-warn"
    >
      <p className="font-medium">{t('notifHealth.fullScreen.title')}</p>
      <button
        type="button"
        className="min-h-touch shrink-0 rounded-xl bg-accent px-3 font-medium text-ink active:bg-accent-press"
        onClick={() => void bridge.openSettings().catch(() => undefined)}
      >
        {t('notifHealth.fullScreen.action')}
      </button>
    </div>
  );
}
